import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { allergyService } from '../services/allergyService'
import type { Allergy } from '../types/allergy'
import { AllergyList } from './AllergyList'
import { AllergyForm } from './AllergyForm'
import { Modal } from './Modal'
import { PatientNkdaBanner } from './PatientNkdaBanner'
import { MaskedField } from './MaskedField'

interface PatientAllergiesProps {
  patientId: string
  readOnly?: boolean
}

export function PatientAllergies({ patientId, readOnly = false }: PatientAllergiesProps) {
  const queryClient = useQueryClient()
  const [editingAllergy, setEditingAllergy] = useState<Allergy | undefined>()
  const [showForm, setShowForm] = useState(false)
  const [deactivateTarget, setDeactivateTarget] = useState<Allergy | undefined>()
  const [deactivateReason, setDeactivateReason] = useState('')
  const canWrite = !readOnly

  const { data: allergies = [], isLoading, error } = useQuery<Allergy[]>({
    queryKey: ['allergies', patientId],
    queryFn: () => {
      if (!patientId) {
        console.error('[PatientAllergies] No patientId provided')
        return Promise.resolve([])
      }
      return allergyService.listByPatient(patientId)
    },
    enabled: !!patientId,
  })

  if (error) {
    console.error('[PatientAllergies] Error loading allergies:', error)
  }

  const createMutation = useMutation({
    mutationFn: (data: { allergy: Parameters<typeof allergyService.create>[0] }) =>
      allergyService.create(data.allergy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allergies', patientId] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; updates: Parameters<typeof allergyService.update>[1] }) =>
      allergyService.update(data.id, data.updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allergies', patientId] })
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: (data: { id: string; reason: string }) =>
      allergyService.deactivate(data.id, data.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allergies', patientId] })
    },
  })

  const nkdaMutation = useMutation({
    mutationFn: (data: { flag: boolean; reason?: string }) =>
      allergyService.setNKDA(patientId, data.flag, data.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allergies', patientId] })
    },
  })

  const handleSave = async (formData: { patientId: string } & any) => {
    if (editingAllergy) {
      await updateMutation.mutateAsync({
        id: editingAllergy.id,
        updates: formData,
      })
    } else {
      await createMutation.mutateAsync({
        allergy: formData,
      })
    }
    setShowForm(false)
    setEditingAllergy(undefined)
  }

  if (isLoading) {
    return <div className="loading">Loading allergies...</div>
  }

  if (error) {
    return <div className="error">Error loading allergies</div>
  }

  return (
    <div className="patient-allergies">
      <MaskedField
        field="allergies"
        value={allergies}
        patientId={patientId}
        confidentialityLevel="restricted"
      >
        {(canAccess, maskedAllergies, requestEmergencyAccess) => {
          if (!canAccess) {
            return (
              <div className="patient-allergies-restricted">
                <p>You do not have permission to view allergy information.</p>
                {requestEmergencyAccess && (
                  <button
                    onClick={requestEmergencyAccess}
                    className="btn-secondary btn-small"
                    style={{ marginTop: '1rem' }}
                  >
                    Request Emergency Access
                  </button>
                )}
              </div>
            )
          }

          return (
            <>
              <PatientNkdaBanner
                allergies={maskedAllergies}
                onSetNkda={async (flag, reason) => {
                  if (!canWrite) return
                  await nkdaMutation.mutateAsync({ flag, reason })
                }}
                readOnly={!canWrite}
              />

              <AllergyList
                allergies={maskedAllergies}
                onAdd={() => {
                  if (!canWrite) return
                  setEditingAllergy(undefined)
                  setShowForm(true)
                }}
                onEdit={(allergy) => {
                  if (!canWrite) return
                  setEditingAllergy(allergy)
                  setShowForm(true)
                }}
                onDeactivate={(allergy) => {
                  if (!canWrite) return
                  setDeactivateTarget(allergy)
                  setDeactivateReason('')
                }}
                readOnly={!canWrite}
              />
            </>
          )
        }}
      </MaskedField>

      {canWrite && showForm && (
        <Modal
          isOpen={showForm}
          onClose={() => {
            setShowForm(false)
            setEditingAllergy(undefined)
          }}
          title={editingAllergy ? 'Edit Allergy / Reaction' : 'Add Allergy / Reaction'}
          size="large"
        >
          <AllergyForm
            patientId={patientId}
            initialAllergy={editingAllergy}
            onSubmit={handleSave}
            onCancel={() => {
              setShowForm(false)
              setEditingAllergy(undefined)
            }}
          />
        </Modal>
      )}

      {canWrite && deactivateTarget && (
        <Modal
          isOpen={!!deactivateTarget}
          onClose={() => setDeactivateTarget(undefined)}
          title="Deactivate Allergy"
        >
          <form
            className="allergy-deactivate-form"
            onSubmit={async (e) => {
              e.preventDefault()
              if (!deactivateTarget) return
              await deactivateMutation.mutateAsync({
                id: deactivateTarget.id,
                reason: deactivateReason || 'No reason provided',
              })
              setDeactivateTarget(undefined)
              setDeactivateReason('')
            }}
          >
            <p>
              Deactivating this allergy will keep it for audit/history but mark it as inactive for
              prescribing checks.
            </p>
            <div className="form-field">
              <label>Reason for deactivation</label>
              <textarea
                value={deactivateReason}
                onChange={(e) => setDeactivateReason(e.target.value)}
                rows={3}
              />
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setDeactivateTarget(undefined)}
                disabled={deactivateMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-danger"
                disabled={deactivateMutation.isPending}
              >
                {deactivateMutation.isPending ? 'Deactivating...' : 'Deactivate Allergy'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}

